import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Upload, FileText, Download } from "lucide-react";
import {
  projectSchema,
  resolveCustomerName,
  SOUTH_INDIA_STATES,
  CUSTOMER_PRESETS,
} from "../validation/projectSchema";
import Modal from "@/components/ui/Modal";
import {
  FormField,
  Input,
  Select,
} from "@/components/ui/FormComponents";
import { Button } from "@/components/ui/Button";
import { projectService } from "../services/projectService";
import { formatInstallationTrade } from "../../verve-teams/utils/teamMemberLabels";
import {
  sanitizePlainText,
  stripCoordString,
  clampLatLngString,
  LAT_MIN,
  LAT_MAX,
  LNG_MIN,
  LNG_MAX,
  MAX_LAT_INPUT_LEN,
  MAX_LNG_INPUT_LEN,
  MAX_HEIGHT_INPUT_LEN,
} from "@/utils/sanitizeFormInput.js";
import * as towerCustomerService from "../../tower-settings/services/towerCustomerService";
import { downloadFile } from "@/lib/API";

const LAUNCH_DOC_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.png,.jpg,.jpeg,.doc,.docx";

const formatYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Polinaidu: 21-04-2026 (20:57) - Auto-assign project manager if creator has the role
const buildCreateDefaults = (user) => ({
  _mode: "create",
  status: "Not started",
  towerType: "",
  projectId: "", // Let backend assign
  customerPreset: "",
  advancePaid: "",
  projectManager:
    user?.role === 'designer' ? user._id || user.id || "" : "",
  applicationId: "",
  srType: "",
  siteId: "",
  siteName: "",
  customerSiteId: "",
  district: "",
  poNumber: "",
  poStatus: "",
  poDate: "",
  wccNumber: "",
  wccStatus: "",
  invoiceNumber: "",
  invoiceDate: "",
  invoiceStatus: "",
  siteStatus: "",
  applicationDate: formatYmd(new Date()),
  commencementDetails: "",
  remarks: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  country: "India",
  latitude: "",
  longitude: "",
  projectName: "",
  height: "",
  projectValue: "",
  targetCost: "",
  civilTargetCost: "",
  electricalTargetCost: "",
  mechanicalTargetCost: "",
  civilExpenseLimit: "",
  electricalExpenseLimit: "",
  mechanicalExpenseLimit: "",
  startDate: formatYmd(new Date()),
  expectedEndDate: formatYmd(addDays(new Date(), 30)),
  capacity: "",
  fieldOfficerIds: [],
});

const MAX_MONEY_INPUT_LEN = 18;

/** Launch-time uploads only (exclude balance-update signed docs). */
function getCreationLaunchDocuments(project) {
  if (!project) return [];

  // Use pre-merged documents from backend if available
  if (Array.isArray(project.mergedDocuments)) {
    return project.mergedDocuments
      .filter(d => d.source !== 'balance_update') // Only show launch/creation docs here
      .map(d => ({
        path: d.path,
        originalName: d.originalName || d.path.split('/').pop() || 'Document'
      }));
  }

  const out = [];
  const seen = new Set();
  for (const d of project.documentUploads || []) {
    if (d?.source !== "creation" || !d?.path || seen.has(d.path)) continue;
    seen.add(d.path);
    out.push({
      path: d.path,
      originalName: d.originalName || d.path.split("/").pop() || "Document",
    });
  }
  for (const path of project.documents || []) {
    if (typeof path !== "string" || !path || seen.has(path)) continue;
    const isBalance = (project.documentUploads || []).some(
      (u) => u?.path === path && u?.source === "balance_update",
    );
    if (isBalance) continue;
    seen.add(path);
    out.push({ path, originalName: path.split("/").pop() || "Document" });
  }
  return out;
}

function mergeSanitized(reg, maxLen) {
  return {
    onChange: (e) => {
      e.target.value = sanitizePlainText(e.target.value, maxLen);
      reg.onChange(e);
    },
  };
}

export const AddProjectModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData,
  user,
}) => {
  const isCreate = !initialData;
  const isOwner = user?.role === "owner";
  const disableOwnerFields = !isCreate && !isOwner;
  const disabledClass = disableOwnerFields ? "bg-slate-50 opacity-70 cursor-not-allowed" : "";

  const [managers, setManagers] = useState([]);
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [towerCustomers, setTowerCustomers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingLaunchDocs, setExistingLaunchDocs] = useState([]);
  const [isFetchingProject, setIsFetchingProject] = useState(false);
  const fileInputRef = useRef(null);

  // Polinaidu: 21-04-2026 (20:57) - Use current user for default assignment
  const defaults = useMemo(() => buildCreateDefaults(user), [user]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(projectSchema),
    defaultValues: defaults,
    mode: "onChange",
  });

  const [formError, setFormError] = useState("");

  const latR = register("latitude");
  const lngR = register("longitude");
  const heightR = register("height");
  const pvR = register("projectValue");
  const tcR = register("targetCost");
  const ctcR = register("civilTargetCost");
  const etcR = register("electricalTargetCost");
  const mtcR = register("mechanicalTargetCost");
  const celR = register("civilExpenseLimit");
  const eelR = register("electricalExpenseLimit");
  const melR = register("mechanicalExpenseLimit");
  const advR = register("advancePaid");
  const appIdR = register("applicationId");
  const srTypeR = register("srType");
  const siteIdR = register("siteId");
  const siteNameR = register("siteName");
  const customerSiteIdR = register("customerSiteId");
  const districtR = register("district");
  const poNumberR = register("poNumber");
  const poStatusR = register("poStatus");
  const poDateR = register("poDate");
  const wccNumberR = register("wccNumber");
  const invoiceNumberR = register("invoiceNumber");
  const pnameR = register("projectName");
  const commencementDetailsR = register("commencementDetails");
  const remarksR = register("remarks");
  const line1R = register("line1");
  const line2R = register("line2");
  const cityR = register("city");
  const countryR = register("country");
  const capR = register("capacity");

  const watchCivil = watch("civilTargetCost");
  const watchElec = watch("electricalTargetCost");
  const watchMech = watch("mechanicalTargetCost");

  useEffect(() => {
    if (watchCivil || watchElec || watchMech) {
      const c = Number(watchCivil) || 0;
      const e = Number(watchElec) || 0;
      const m = Number(watchMech) || 0;
      setValue("targetCost", String(c + e + m), { shouldValidate: true });
    }
  }, [watchCivil, watchElec, watchMech, setValue]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownloadDoc = async (doc) => {
    // Use the secure streaming endpoint for guaranteed high-fidelity downloads
    const streamUrl = `/projects/documents/stream?filePath=${encodeURIComponent(doc.path)}`;
    downloadFile(streamUrl, doc.originalName);
  };

  const populateFromProject = useCallback(
    (p) => {
      setValue("_mode", "edit");
      setValue("projectId", p.projectId || "");
      const cn = p.customer?._id || p.customer || p.customerName || "";
      setValue("customerPreset", cn);
      setValue("projectName", p.projectName || "");
      setValue("applicationId", p.applicationId || "");
      setValue("srType", p.srType || "");
      setValue("siteId", p.siteId || "");
      setValue("siteName", p.siteName || "");
      setValue("customerSiteId", p.customerSiteId || "");
      setValue("district", p.district || "");
      setValue("poNumber", p.poNumber || "");
      setValue("poStatus", p.poStatus || "");
      setValue("poDate", p.poDate ? formatYmd(new Date(p.poDate)) : "");
      setValue("wccNumber", p.wccNumber || "");
      setValue("wccStatus", p.wccStatus || "");
      setValue("invoiceNumber", p.invoiceNumber || "");
      setValue("invoiceDate", p.invoiceDate ? formatYmd(new Date(p.invoiceDate)) : "");
      setValue("invoiceStatus", p.invoiceStatus || "");
      setValue("siteStatus", p.siteStatus || "");
      setValue("commencementDetails", p.commencementDetails || "");
      setValue("remarks", p.remarks || "");
      setValue(
        "applicationDate",
        p.applicationDate
          ? new Date(p.applicationDate).toISOString().split("T")[0]
          : formatYmd(new Date()),
      );
      if (p.address) {
        setValue("line1", p.address.line1 || "");
        setValue("line2", p.address.line2 || "");
        setValue("city", p.address.city || "");
        const st = p.address.state || "";
        setValue("state", SOUTH_INDIA_STATES.includes(st) ? st : "Telangana");
        setValue("country", p.address.country || "India");
        setValue(
          "latitude",
          p.address.latitude != null ? String(p.address.latitude) : "",
        );
        setValue(
          "longitude",
          p.address.longitude != null ? String(p.address.longitude) : "",
        );
      }
      setValue("towerType", p.towerType || "");
      setValue("height", p.height != null ? String(p.height) : "");
      setValue(
        "projectValue",
        p.projectValue != null ? String(p.projectValue) : "",
      );
      setValue("targetCost", p.targetCost != null ? String(p.targetCost) : "");
      setValue("civilTargetCost", p.civilTargetCost != null ? String(p.civilTargetCost) : "");
      setValue("electricalTargetCost", p.electricalTargetCost != null ? String(p.electricalTargetCost) : "");
      setValue("mechanicalTargetCost", p.mechanicalTargetCost != null ? String(p.mechanicalTargetCost) : "");
      setValue("civilExpenseLimit", p.civilExpenseLimit != null ? String(p.civilExpenseLimit) : "");
      setValue("electricalExpenseLimit", p.electricalExpenseLimit != null ? String(p.electricalExpenseLimit) : "");
      setValue("mechanicalExpenseLimit", p.mechanicalExpenseLimit != null ? String(p.mechanicalExpenseLimit) : "");
      setValue(
        "advancePaid",
        p.advancePaid != null && p.advancePaid !== 0
          ? String(p.advancePaid)
          : "",
      );
      setValue(
        "startDate",
        p.startDate
          ? new Date(p.startDate).toISOString().split("T")[0]
          : formatYmd(new Date()),
      );
      setValue(
        "expectedEndDate",
        p.expectedEndDate
          ? new Date(p.expectedEndDate).toISOString().split("T")[0]
          : formatYmd(addDays(new Date(), 30)),
      );
      setValue("projectManager", p.projectManager?._id || p.managerId || "");
      setValue(
        "fieldOfficerIds",
        (p.fieldOfficers || []).map((x) => {
          if (x == null) return "";
          if (typeof x === "object" && x._id != null) return String(x._id);
          return String(x);
        }).filter(Boolean),
      );
      setValue("status", p.status || "Not started");
      setValue("capacity", p.capacity || "");
    },
    [setValue],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      // Always fetch fresh data from the server so newly-added fields
      // (like poStatus) are not stale from the list cache.
      setIsFetchingProject(true);
      setSelectedFiles([]);
      setFormError("");
      clearErrors();
      projectService.getProjectById(initialData._id)
        .then((freshProject) => {
          setExistingLaunchDocs(getCreationLaunchDocuments(freshProject));
          populateFromProject(freshProject);
        })
        .catch(() => {
          // Fallback to stale list data if fetch fails
          setExistingLaunchDocs(getCreationLaunchDocuments(initialData));
          populateFromProject(initialData);
        })
        .finally(() => setIsFetchingProject(false));
    } else {
      reset(buildCreateDefaults(user));
      setSelectedFiles([]);
      setExistingLaunchDocs([]);
      setFormError("");
      clearErrors();
    }
  }, [initialData, isOpen, populateFromProject, reset, clearErrors]);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const data = await projectService.getProjectManagers();
        const active = (data || []).filter((u) => u?.status !== "Inactive");
        setManagers(active);

        // Polinaidu: 25-04-2026 (15:15) - Auto-preselect if exactly one active PM exists
        if (!initialData && active.length === 1) {
          setValue("projectManager", active[0]._id || active[0].id, { shouldValidate: true, shouldDirty: true });
        }
      } catch (err) {
        console.error("Failed to fetch managers:", err);
      }
    };
    const fetchFieldOfficers = async () => {
      try {
        const data = await projectService.getFieldOfficers();
        setFieldOfficers((data || []).filter((u) => u?.status !== "Inactive"));
      } catch (err) {
        console.error("Failed to fetch installation team:", err);
      }
    };
    const fetchTowerCustomers = async () => {
      try {
        const res = await towerCustomerService.getTowerCustomers({ status: 'ACTIVE' });
        setTowerCustomers(res.data || []);
      } catch (err) {
        console.error("Failed to fetch tower customers:", err);
      }
    };
    if (isOpen) {
      fetchManagers();
      fetchFieldOfficers();
      fetchTowerCustomers();
    }
  }, [isOpen]);

  const handleFormSubmit = async (data) => {
    setFormError("");

    // Final file size check before submission
    const MAX_SIZE = 10 * 1024 * 1024;
    const oversized = selectedFiles.filter(f => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      setFormError("Cannot submit: Some files exceed the 10MB limit.");
      return;
    }

    const formData = new FormData();
    let customerId = "";
    let customerName = data.customerPreset;
    const customerObj = towerCustomers.find((c) => c._id === data.customerPreset);
    if (customerObj) {
      customerId = customerObj._id;
      customerName = customerObj.customerName;
    } else {
      const byName = towerCustomers.find((c) => c.customerName === data.customerPreset);
      if (byName) {
        customerId = byName._id;
        customerName = byName.customerName;
      }
    }

    const teamIds = Array.isArray(data.fieldOfficerIds) ? data.fieldOfficerIds : [];

    Object.keys(data).forEach((key) => {
      if (
        ["_mode", "customerPreset", "customerOtherName", "documents", "fieldOfficerIds"].includes(
          key,
        )
      )
        return;
      let value = data[key];
      if (
        ["height", "projectValue", "targetCost", "civilTargetCost", "electricalTargetCost", "mechanicalTargetCost", "civilExpenseLimit", "electricalExpenseLimit", "mechanicalExpenseLimit", "advancePaid"].includes(key)
      ) {
        value = value === "" && key === "advancePaid" ? "0" : String(value);
      }
      if (key === "projectManager" && !value) return;
      formData.append(key, value);
    });

    formData.append("customerName", customerName);
    if (customerId) formData.append("customerId", customerId);
    formData.append("fieldOfficers", JSON.stringify(teamIds));

    if (selectedFiles.length > 0) {
      selectedFiles.forEach((file) => {
        formData.append("documents", file);
      });
    }

    try {
      await onSubmit(formData);
      setSelectedFiles([]);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to process request";
      setFormError(msg);

      // Map specific backend errors to fields
      if (msg.toLowerCase().includes("application id")) {
        setError("applicationId", { type: "manual", message: msg });
      }
      if (msg.toLowerCase().includes("project id")) {
        setError("projectId", { type: "manual", message: msg });
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="h-fit"
      title={initialData ? "Edit Tower Project" : "Create New Tower Project"}
      footer={
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="w-full sm:flex-1 h-10 rounded-lg"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            form="add-project-form"
            className="w-full sm:flex-1 h-10 rounded-lg"
          >
            {isLoading
              ? initialData
                ? "Updating..."
                : "Creating..."
              : initialData
                ? "Update Project"
                : "Launch Project"}
          </Button>
        </div>
      }
    >
      <form
        id="add-project-form"
        onSubmit={handleSubmit(handleFormSubmit, () => {
          setFormError("Please check the form for errors and fill all required fields.");
        })}
        className="space-y-5"
      >
        <input type="hidden" {...register("_mode")} />

        {formError && (
          <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-[11px] font-bold text-rose-600 animate-in fade-in slide-in-from-top-1">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Client Name" error={errors.customerPreset || errors.customerOtherName} required>
            <Select {...register("customerPreset")} disabled={disableOwnerFields} className={disabledClass}>
              <option value="">Select Client Name</option>
              {towerCustomers.map((c) => (
                <option key={c._id} value={c._id}>{c.customerName}</option>
              ))}
            </Select>
          </FormField>

          <FormField label="Allocation Date" error={errors.applicationDate} required>
            <Input type="date" disabled={disableOwnerFields} className={disabledClass} {...register("applicationDate")} />
          </FormField>


          <FormField label="Customer Name" error={errors.projectName} required>
            <Input placeholder="e.g. Customer Name" readOnly={disableOwnerFields} className={disabledClass} {...pnameR} {...mergeSanitized(pnameR, 100)} />
          </FormField>
          <FormField label="Customer Project ID" error={errors.applicationId} required>
            <Input placeholder="Enter Customer Project ID" readOnly={disableOwnerFields} className={disabledClass} {...appIdR} {...mergeSanitized(appIdR, 100)} />
          </FormField>
          <FormField label="Customer Site ID" error={errors.customerSiteId} required>
            <Input placeholder="e.g. Cust Site ID" readOnly={disableOwnerFields} className={disabledClass} {...customerSiteIdR} {...mergeSanitized(customerSiteIdR, 100)} />
          </FormField>
          <FormField label="SR Type" error={errors.srType}>
            <Input placeholder="e.g. SR Type" readOnly={disableOwnerFields} className={disabledClass} {...srTypeR} {...mergeSanitized(srTypeR, 100)} />
          </FormField>

          <FormField label="Site ID" error={errors.siteId} required>
            <Input placeholder="e.g. Site ID" readOnly={disableOwnerFields} className={disabledClass} {...siteIdR} {...mergeSanitized(siteIdR, 100)} />
          </FormField>

          <FormField label="Site Name" error={errors.siteName} required>
            <Input placeholder="e.g. Site Name" readOnly={disableOwnerFields} className={disabledClass} {...siteNameR} {...mergeSanitized(siteNameR, 100)} />
          </FormField>
          <FormField label="Site Status" error={errors.siteStatus}>
            <Select {...register("siteStatus")} value={watch("siteStatus") || ""} disabled={disableOwnerFields} className={disabledClass}>
              <option value="">Select Site Status</option>
              <option value="WIP">WIP</option>
              <option value="WNS">WNS</option>
              <option value="HOLD">HOLD</option>
              <option value="NFA">NFA</option>
              <option value="RFAI">RFAI</option>
            </Select>
          </FormField>





          <FormField label="Tower Type" error={errors.towerType}>
            <Select {...register("towerType")} value={watch("towerType") || ""} disabled={disableOwnerFields} className={disabledClass}>
              <option value="">Select tower type</option>
              <option value="RTT">RTT</option>
              <option value="RTP">RTP</option>
              <option value="GBT">GBT</option>
            </Select>
          </FormField>

          <FormField label="District" error={errors.district} required>
            <Input placeholder="e.g. District" readOnly={disableOwnerFields} className={disabledClass} {...districtR} {...mergeSanitized(districtR, 100)} />
          </FormField>

          <FormField label="City" error={errors.city} required>
            <Input
              placeholder="e.g. Hyderabad"
              readOnly={disableOwnerFields}
              className={disabledClass}
              {...cityR}
              {...mergeSanitized(cityR, 100)}
            />
          </FormField>
          <FormField label="State" error={errors.state} required>
            <Select {...register("state")} disabled={disableOwnerFields} className={disabledClass}>
              <option value="">Select state</option>
              {SOUTH_INDIA_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label="Assign Project Manager"
            error={errors.projectManager}
          >
            <Select {...register("projectManager")} value={watch("projectManager") || ""}>
              <option value="">Not Assigned</option>
              {managers.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name} ({m.phone})
                </option>
              ))}
            </Select>
          </FormField>


          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-2 border-t border-slate-100 pt-4 flex flex-col gap-3">
            <FormField label="PO Number" error={errors.poNumber}>
              <Input placeholder="e.g. PO-12345" readOnly={disableOwnerFields} className={disabledClass} {...poNumberR} {...mergeSanitized(poNumberR, 50)} />
            </FormField>

            <FormField label="PO Value (₹)" error={errors.projectValue} required>
              <Input type="text" inputMode="decimal" placeholder="e.g. 500000" readOnly={disableOwnerFields} className={disabledClass} {...pvR} onChange={(e) => { if (disableOwnerFields) return; e.target.value = stripCoordString(e.target.value, false, MAX_MONEY_INPUT_LEN); pvR.onChange(e); }} />
            </FormField>

            <FormField label="Target Budget (₹)" error={errors.targetCost}>
              <Input type="text" inputMode="decimal" placeholder="e.g. 400000" readOnly={disableOwnerFields} className={disabledClass} {...tcR} onChange={(e) => { if (disableOwnerFields) return; e.target.value = stripCoordString(e.target.value, false, MAX_MONEY_INPUT_LEN); tcR.onChange(e); }} />
            </FormField>

            <FormField label="PO Status" error={errors.poStatus}>
              <Input placeholder="e.g. Approved" readOnly={disableOwnerFields} className={disabledClass} {...poStatusR} {...mergeSanitized(poStatusR, 50)} />
            </FormField>
          </div>




          <FormField label="WCC Number" error={errors.wccNumber}>
            <Input placeholder="e.g. WCC-12345" readOnly={disableOwnerFields} className={disabledClass} {...wccNumberR} {...mergeSanitized(wccNumberR, 50)} />
          </FormField>

          <FormField label="WCC Status" error={errors.wccStatus}>
            <Input placeholder="e.g. Approved" readOnly={disableOwnerFields} className={disabledClass} {...register("wccStatus")} {...mergeSanitized(register("wccStatus"), 50)} />
          </FormField>

          {/* <FormField label="Invoice Number" error={errors.invoiceNumber}>
            <Input placeholder="e.g. INV-12345" readOnly={disableOwnerFields} className={disabledClass} {...invoiceNumberR} {...mergeSanitized(invoiceNumberR, 50)} />
          </FormField>

          <FormField label="Invoice Date" error={errors.invoiceDate}>
            <Input type="date" disabled={disableOwnerFields} className={disabledClass} {...register("invoiceDate")} />
          </FormField> */}
        </div>

        {!isCreate && (
          <div className="flex flex-col gap-4 pt-4 border-t border-slate-50">
            <h4 className="text-[10px] font-extrabold text-[#2b448f] tracking-wide bg-slate-50 px-3 py-1.5 rounded-xl inline-block">
              Work location
            </h4>

            <div className="grid grid-cols-1 gap-4">
              {/* Polinaidu: 21-04-2026 (21:25) - Address Line 1 is now optional */}
              <FormField label="Address Line 1" error={errors.line1}>
                <Input
                  placeholder="House No, Street, Area"
                  {...line1R}
                  {...mergeSanitized(line1R, 100)}
                />
              </FormField>
              <FormField label="Address Line 2 (Optional)" error={errors.line2}>
                <Input
                  placeholder="Landmark, Building details (min 3 if filled)"
                  {...line2R}
                  {...mergeSanitized(line2R, 100)}
                />
              </FormField>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">





          <div className="col-span-1 sm:col-span-2 border-t border-slate-100 pt-4 flex flex-col gap-3">
            <h5 className="text-[10px] font-extrabold text-[#2b448f] tracking-wide bg-slate-50 px-3 py-1.5 rounded-xl flex items-center gap-2 w-fit">
              <span>Target Cost Breakdowns (Budgets by Trade)</span>
              {Number(watch("targetCost")) > 0 && (
                <span className="bg-blue-100 text-[#2b448f] px-2 py-0.5 rounded-md">
                  Total Budget: ₹{Number(watch("targetCost")).toLocaleString("en-IN")}
                </span>
              )}
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                label="Civil Target Budget (₹)"
                error={errors.civilTargetCost}
                required
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 100000"
                  readOnly={disableOwnerFields}
                  className={disabledClass}
                  {...ctcR}
                  onChange={(e) => {
                    if (disableOwnerFields) return;
                    e.target.value = stripCoordString(
                      e.target.value,
                      false,
                      MAX_MONEY_INPUT_LEN,
                    );
                    ctcR.onChange(e);
                  }}
                />
              </FormField>
              <FormField
                label="Electrical Target Budget (₹)"
                error={errors.electricalTargetCost}
                required
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 150000"
                  readOnly={disableOwnerFields}
                  className={disabledClass}
                  {...etcR}
                  onChange={(e) => {
                    if (disableOwnerFields) return;
                    e.target.value = stripCoordString(
                      e.target.value,
                      false,
                      MAX_MONEY_INPUT_LEN,
                    );
                    etcR.onChange(e);
                  }}
                />
              </FormField>
              <FormField
                label="Tower Target Budget (₹)"
                error={errors.mechanicalTargetCost}
                required
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 80000"
                  readOnly={disableOwnerFields}
                  className={disabledClass}
                  {...mtcR}
                  onChange={(e) => {
                    if (disableOwnerFields) return;
                    e.target.value = stripCoordString(
                      e.target.value,
                      false,
                      MAX_MONEY_INPUT_LEN,
                    );
                    mtcR.onChange(e);
                  }}
                />
              </FormField>
            </div>
          </div>

          <div className="col-span-1 sm:col-span-2 border-t border-slate-100 pt-4 flex flex-col gap-3">
            <h5 className="text-[10px] font-extrabold text-[#2b448f] tracking-wide bg-slate-50 px-3 py-1.5 rounded-xl inline-block w-fit">
              Expense Limits (Budgets by Trade)
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                label="Civil Expense Limit (₹)"
                error={errors.civilExpenseLimit}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 100000"
                  readOnly={disableOwnerFields}
                  className={disabledClass}
                  {...celR}
                  onChange={(e) => {
                    if (disableOwnerFields) return;
                    e.target.value = stripCoordString(
                      e.target.value,
                      false,
                      MAX_MONEY_INPUT_LEN,
                    );
                    celR.onChange(e);
                  }}
                />
              </FormField>
              <FormField
                label="Electrical Expense Limit (₹)"
                error={errors.electricalExpenseLimit}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 150000"
                  readOnly={disableOwnerFields}
                  className={disabledClass}
                  {...eelR}
                  onChange={(e) => {
                    if (disableOwnerFields) return;
                    e.target.value = stripCoordString(
                      e.target.value,
                      false,
                      MAX_MONEY_INPUT_LEN,
                    );
                    eelR.onChange(e);
                  }}
                />
              </FormField>
              <FormField
                label="Tower Expense Limit (₹)"
                error={errors.mechanicalExpenseLimit}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 80000"
                  readOnly={disableOwnerFields}
                  className={disabledClass}
                  {...melR}
                  onChange={(e) => {
                    if (disableOwnerFields) return;
                    e.target.value = stripCoordString(
                      e.target.value,
                      false,
                      MAX_MONEY_INPUT_LEN,
                    );
                    melR.onChange(e);
                  }}
                />
              </FormField>
            </div>
          </div>

          {!isCreate && (
            <>
              {/* <FormField label="Advance Paid (₹)" error={errors.advancePaid}>
            <div className="relative">
              <Input
                type="text"
                inputMode="decimal"
                id="launch-advance"
                placeholder="Optional — cannot exceed total value"
                readOnly={!!initialData?._id}
                className={initialData?._id ? "bg-slate-50 opacity-70 cursor-not-allowed" : ""}
                {...advR}
                onChange={(e) => {
                  if (initialData?._id) return; // safety check
                  e.target.value = stripCoordString(
                    e.target.value,
                    false,
                    MAX_MONEY_INPUT_LEN,
                  );
                  advR.onChange(e);
                }}
              />
              {initialData?._id && (
                <p className="text-[10px] font-medium text-slate-400 mt-1">
                  Derived from transactions. Add new payments via the Transaction Modal.
                </p>
              )}
            </div>
          </FormField> */}
              {/* <FormField label="Target Capacity" error={errors.capacity}>
            <Input
              placeholder="Optional — positive number e.g. 40"
              {...capR}
              {...mergeSanitized(capR, 100)}
            />
          </FormField> */}
              {/* Polinaidu: 22-04-2026 (13:48) - Start Date is now optional */}
              <FormField label="Start Date" error={errors.startDate}>
                <Input type="date" min={initialData ? undefined : new Date().toISOString().split("T")[0]} {...register("startDate")} />
              </FormField>
              {/* Polinaidu: 21-04-2026 (21:25) - Expected End Date is now optional */}
              <FormField label="Expected End Date" error={errors.expectedEndDate}>
                <Input type="date" min={initialData ? (watch("startDate") || undefined) : (watch("startDate") || new Date().toISOString().split("T")[0])} {...register("expectedEndDate")} />
              </FormField>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Commencement Details" error={errors.commencementDetails}>
            <textarea
              className={`w-full min-h-[80px] p-2 text-[13px] rounded-lg border focus:ring-2 focus:ring-brand-blue/20 outline-none resize-y transition-all ${errors.commencementDetails ? "border-red-500" : "border-slate-200 focus:border-brand-blue"
                } ${disableOwnerFields ? "bg-slate-50 opacity-70 cursor-not-allowed" : "bg-white"}`}
              placeholder="Enter commencement details..."
              readOnly={disableOwnerFields}
              {...commencementDetailsR}
            />
          </FormField>

          <FormField label="Remarks" error={errors.remarks}>
            <textarea
              className={`w-full min-h-[80px] p-2 text-[13px] rounded-lg border focus:ring-2 focus:ring-brand-blue/20 outline-none resize-y transition-all ${errors.remarks ? "border-red-500" : "border-slate-200 focus:border-brand-blue"
                } ${disableOwnerFields ? "bg-slate-50 opacity-70 cursor-not-allowed" : "bg-white"}`}
              placeholder="Enter remarks..."
              readOnly={disableOwnerFields}
              {...remarksR}
            />
          </FormField>
        </div>

        {!isCreate && (
          <div className="grid grid-cols-1 gap-4">
            <FormField
              label="Installation Team"
              error={errors.fieldOfficerIds}
            >
              <p className="text-[9px] font-semibold text-slate-400 mb-1">
                At most one member per trade (Civil, Electrical, Mechanical). On desktop, hold
                Ctrl or âŒ˜ to select multiple.
              </p>
              <select
                multiple
                className="w-full min-h-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                value={watch("fieldOfficerIds") || []}
                onChange={(e) => {
                  const selected = Array.from(
                    e.target.selectedOptions,
                    (o) => o.value,
                  );
                  setValue("fieldOfficerIds", selected, { shouldValidate: true });
                }}
              >
                {fieldOfficers.map((fo) => (
                  <option key={fo._id} value={fo._id}>
                    {fo.name} · {formatInstallationTrade(fo.installationTrade)} · {fo.phone}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        )}

        {!isCreate && (
          <div className="grid grid-cols-1 gap-4">
            <FormField
              label="Launch Documents (Multiple Selection)"
              error={errors.documents}
            >
              <p className="text-[9px] font-semibold text-rose-500/80 mb-1">
                Supported: PDF, Images, Word · Max 10MB per file
              </p>
              <div className="flex flex-col gap-3">
                <label
                  htmlFor="launch-documents-input"
                  className="h-10 px-4 rounded-lg border-2 border-dashed border-slate-200 bg-white text-[12px] font-bold text-slate-600 inline-flex items-center justify-center gap-2 cursor-pointer transition-all duration-300 ease-in-out hover:border-[#f97316] hover:text-[#f97316]"
                >
                  <Upload className="w-4 h-4 shrink-0" />
                  Choose Files (Hold Ctrl or Shift to Select Multiple)
                </label>
                <input
                  id="launch-documents-input"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  className="hidden"
                  accept={LAUNCH_DOC_ACCEPT}
                />

                {(existingLaunchDocs.length > 0 || selectedFiles.length > 0) && (
                  <div className="flex flex-col gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in duration-300">
                    {existingLaunchDocs.length > 0 && (
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight px-0.5">
                        Saved at launch
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {existingLaunchDocs.map((doc) => (
                        <button
                          type="button"
                          key={`saved-${doc.path}`}
                          onClick={() => handleDownloadDoc(doc)}
                          className="flex items-center gap-2 px-2 py-1 bg-white border border-emerald-100 rounded-md hover:border-emerald-300 transition-all max-w-full min-w-0"
                        >
                          <FileText className="w-3 h-3 shrink-0 text-emerald-600" />
                          <span className="text-[9px] font-extrabold text-[#2b448f] truncate max-w-[200px]">
                            {doc.originalName}
                          </span>
                          <Download className="w-3 h-3 text-emerald-600 shrink-0" />
                          <span className="text-[8px] font-bold text-emerald-600 shrink-0">
                            Secure Download
                          </span>
                        </button>
                      ))}
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={`new-${idx}-${file.name}-${file.size}`}
                          className="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded-md group hover:border-rose-200 transition-all"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          <span className="text-[9px] font-extrabold text-[#2b448f] truncate max-w-[200px]">
                            {file.name}
                          </span>
                          <span className="text-[8px] font-bold text-slate-300">
                            ({(file.size / 1024).toFixed(0)} KB)
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="p-0.5 rounded-full hover:bg-rose-50 text-slate-300 hover:text-rose-500 transition-all"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </FormField>
          </div>
        )}

      </form>
    </Modal>
  );
};
